import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    AcademicCapIcon,
    CalendarDaysIcon,
    CheckCircleIcon,
    ChevronRightIcon,
    ArrowPathIcon,
    BookOpenIcon,
    PlayCircleIcon,
    TrophyIcon,
    FireIcon,
    ClockIcon
} from '@heroicons/react/24/outline';
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend
} from 'recharts';

// --- Utility Functions ---

const formatDate = (dateString, format = 'MMM Do') => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    let daySuffix;

    if (day > 3 && day < 21) {
        daySuffix = 'th';
    } else {
        switch (day % 10) {
            case 1: daySuffix = 'st'; break;
            case 2: daySuffix = 'nd'; break;
            case 3: daySuffix = 'rd'; break;
            default: daySuffix = 'th';
        }
    }

    if (format === 'MMM Do') return `${month} ${day}${daySuffix}`;
    if (format === 'ddd, MMM Do') {
         const dayOfWeek = date.toLocaleDateString('default', { weekday: 'short' });
         return `${dayOfWeek}, ${month} ${day}${daySuffix}`;
    }
    return date.toLocaleDateString('default', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true });
};

const calculateDaysLeft = (targetDateString) => {
    const target = new Date(targetDateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0); 
    const diffTime = target.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// --- Custom Components ---

const DashboardSection = ({ children, title, icon: IconComponent, className = '' }) => (
    <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className={`bg-white p-6 rounded-xl shadow-lg border border-gray-100 flex flex-col mb-6 ${className}`}
    >
        <div className="flex items-center space-x-3 border-b pb-3 mb-4">
            {IconComponent && <IconComponent className="w-6 h-6 text-indigo-600" />}
            <h2 className="text-xl font-bold text-gray-900 tracking-tight font-['Poppins']">{title}</h2>
        </div>
        <div className="flex-grow">{children}</div>
    </motion.div>
);

const CourseProgressCard = ({ course, onAction }) => {
    const daysLeft = calculateDaysLeft(course.due);
    let buttonText = 'Continue Learning';
    let buttonStyle = 'bg-blue-600 hover:bg-blue-700';

    if (course.status === 'Assigned') {
        buttonText = 'Start Course';
        buttonStyle = 'bg-indigo-600 hover:bg-indigo-700';
    } else if (course.status === 'Completed') {
        buttonText = 'Review Certificate';
        buttonStyle = 'bg-emerald-500 hover:bg-emerald-600';
    }

    const progressColor = course.progress === 100 ? 'bg-emerald-500' : 'bg-blue-500';
    const DueIcon = daysLeft <= 7 && course.progress < 100 ? FireIcon : CalendarDaysIcon;
    const dueColor = daysLeft <= 7 && course.progress < 100 ? 'text-red-500' : 'text-gray-500';

    return (
        <div className="p-4 bg-gray-50 rounded-lg shadow-inner mb-3 flex flex-col md:flex-row justify-between items-start md:items-center transition duration-200 hover:shadow-md">
            <div className="flex items-center w-full md:w-2/3">
                <course.icon className="w-8 h-8 text-indigo-500 mr-4 flex-shrink-0 hidden sm:block" />
                <div className="flex-grow">
                    <h3 className="text-lg font-semibold text-gray-800 font-['Poppins'] leading-tight">{course.title}</h3>
                    <div className="flex items-center text-sm mt-1">
                        <DueIcon className={`w-4 h-4 mr-1 ${dueColor}`} />
                        <p className={`text-xs font-medium ${dueColor}`}>
                            {course.status === 'Completed' ? 'Completed' : `Due: ${formatDate(course.due, 'MMM Do')} (${daysLeft} days left)`}
                        </p>
                    </div>
                    {course.progress < 100 && (
                        <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                            <div 
                                className={`h-2.5 rounded-full ${progressColor} transition-all duration-500`} 
                                style={{ width: `${course.progress}%` }}
                            ></div>
                        </div>
                    )}
                </div>
            </div>
            
            <div className="mt-4 md:mt-0 md:ml-4 flex-shrink-0 w-full md:w-auto">
                <button
                    onClick={() => onAction(course.id)}
                    className={`w-full md:w-auto px-4 py-2 text-sm font-medium rounded-full flex items-center justify-center transition duration-200 shadow-md text-white ${buttonStyle}`}
                >
                    {course.status !== 'Completed' && <PlayCircleIcon className="w-4 h-4 mr-2" />}
                    {buttonText}
                </button>
            </div>
        </div>
    );
};

const LearningEventItem = ({ event }) => {
    const iconMap = {
        'Deadline': <ClockIcon className="w-5 h-5 text-red-500" />,
        'Workshop': <AcademicCapIcon className="w-5 h-5 text-blue-500" />,
        'Exam': <TrophyIcon className="w-5 h-5 text-amber-500" />,
    };

    return (
        <div className="flex items-start p-3 hover:bg-gray-50 rounded-lg transition duration-150">
            <div className="flex-shrink-0 mr-3 mt-1">
                {iconMap[event.type] || <CalendarDaysIcon className="w-5 h-5 text-gray-400" />}
            </div>
            <div className="flex-grow">
                <p className="text-sm font-medium text-gray-800 leading-tight font-['Poppins']">{event.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                    {formatDate(event.date, 'ddd, MMM Do')}
                    <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-600">{event.type}</span>
                </p>
            </div>
        </div>
    );
};

const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50 p-4 font-['Roboto']">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white p-6 rounded-xl shadow-2xl max-w-lg w-full">
                <div className="flex justify-between items-center border-b pb-3 mb-4">
                    <h3 className="text-xl font-bold text-gray-900 font-['Poppins']">{title}</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">&times;</button>
                </div>
                {children}
            </motion.div>
        </div>
    );
};

// --- Main App ---

const App = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    
    // State for courses to make buttons functional
    const [courses, setCourses] = useState([
        { id: 1, title: 'Advanced JavaScript Techniques', progress: 75, due: '2026-02-20', status: 'In Progress', icon: BookOpenIcon },
        { id: 2, title: 'Cloud Fundamentals (AWS)', progress: 0, due: '2026-03-30', status: 'Assigned', icon: AcademicCapIcon },
        { id: 3, title: 'Effective Communication', progress: 100, due: '2025-11-01', status: 'Completed', icon: CheckCircleIcon },
        { id: 4, title: 'Q4 Compliance Training', progress: 30, due: '2026-02-10', status: 'In Progress', icon: TrophyIcon },
    ]);

    const upcomingLearningEvents = [
        { type: 'Deadline', date: '2026-02-10', title: 'Compliance Training Deadline' },
        { type: 'Workshop', date: '2026-02-05', title: 'Data Security Workshop' },
        { type: 'Exam', date: '2026-02-20', title: 'JS Advanced Final Exam' },
    ];

    const courseStatusData = [
        { name: 'Completed', value: courses.filter(c => c.status === 'Completed').length, color: '#10b981' }, 
        { name: 'In Progress', value: courses.filter(c => c.status === 'In Progress').length, color: '#3b82f6' }, 
        { name: 'Assigned', value: courses.filter(c => c.status === 'Assigned').length, color: '#f59e0b' }, 
    ];

    const handleCourseAction = (id) => {
        setCourses(prev => prev.map(course => {
            if (course.id === id) {
                if (course.status === 'Assigned') {
                    return { ...course, status: 'In Progress', progress: 10 };
                }
                if (course.status === 'In Progress') {
                    const nextProgress = Math.min(course.progress + 15, 100);
                    return { 
                        ...course, 
                        progress: nextProgress, 
                        status: nextProgress === 100 ? 'Completed' : 'In Progress' 
                    };
                }
            }
            return course;
        }));
    };

    const handleRefresh = () => {
        setIsLoading(true);
        setTimeout(() => setIsLoading(false), 1000);
    };

    return (
        <div className="min-h-screen p-4 md:p-8 bg-gray-50 text-gray-800 font-['Roboto']">
            
            <Modal isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} title="Full Training Calendar">
                <ul className="space-y-2 text-sm max-h-60 overflow-y-auto">
                    {upcomingLearningEvents.map((event, index) => (
                        <li key={index} className="flex justify-between p-2 border-b last:border-0">
                            <span className="font-medium">{event.title}</span>
                            <span className="text-xs text-indigo-600">{formatDate(event.date, 'MMM Do')}</span>
                        </li>
                    ))}
                </ul>
            </Modal>

            <header className="max-w-6xl mx-auto mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center border-b pb-4">
                <div>
                    <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 font-['Poppins']">Learning Hub</h1>
                    <p className="text-md text-gray-500 mt-1">Your core hub for development metrics.</p>
                </div>
                <button onClick={handleRefresh} disabled={isLoading} className="mt-4 sm:mt-0 px-4 py-2 bg-indigo-600 text-white rounded-full flex items-center hover:bg-indigo-700 transition shadow-md">
                    <ArrowPathIcon className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                    {isLoading ? 'Refreshing...' : 'Refresh Data'}
                </button>
            </header>

            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 1. Learning Paths */}
                <DashboardSection title="My Active Learning Paths" icon={BookOpenIcon}>
                    <div className="space-y-3">
                        {courses.filter(c => c.status !== 'Completed').map(course => (
                            <CourseProgressCard key={course.id} course={course} onAction={handleCourseAction} />
                        ))}
                    </div>
                </DashboardSection>

                {/* 2. Upcoming Deadlines */}
                <DashboardSection title="Upcoming Deadlines" icon={CalendarDaysIcon}>
                    <div className="space-y-1">
                        {upcomingLearningEvents.map((event, index) => (
                            <LearningEventItem key={index} event={event} />
                        ))}
                    </div>
                    <button onClick={() => setIsCalendarOpen(true)} className="w-full mt-4 text-sm text-indigo-600 font-semibold flex justify-center items-center hover:text-indigo-800">
                        View Full Training Calendar <ChevronRightIcon className="w-4 h-4 ml-1" />
                    </button>
                </DashboardSection>

                {/* 3. Course Status Chart (Kept this as it is distinct from the 2 removed) */}
                <div className="lg:col-span-2">
                    <DashboardSection title="Current Course Status" icon={AcademicCapIcon}>
                        <div className="h-[250px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={courseStatusData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {courseStatusData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </DashboardSection>
                </div>
            </div>

            <footer className="mt-12 pt-4 border-t text-center text-xs text-gray-400">
                <p>© 2026 Learning & Performance Hub | Data refreshed today</p>
            </footer>

            <style jsx global>{`
                @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@700;800&family=Roboto:wght@400;500&display=swap');
            `}</style>
        </div>
    );
};

export default App;